import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../services/HttpManager";
import { MasivoPlantilla, MasivoProgreso, MasivoVista } from "../../services/v2/prospects.service";
import { errorTexto } from "../../navigation/v2/sales/salesHelpers";
import "./whatsapp.css";

interface Props {
	campusId: number;
	studentIds: number[];
	/** De dónde sale: estudiantes | horario | matricula | asistencias | saldos (queda en el registro del envío). */
	origen: string;
	referencia?: string | null;
	/** Datos que ya se saben (Talleres: fecha, hora, taller, precio, plantel): llenan solos los {{datos}} de la plantilla con ese nombre. */
	fijosIniciales?: Record<string, string>;
	onClose: () => void;
	onEnviado?: () => void;
}

/**
 * Mensaje igual a varios alumnos (Esteban, 2026-10-18: "multiselect y enviar mensaje igual a todos"). Una plantilla aprobada
 * de WAPP; los datos de cada quien (nombre, saldo, mensualidades…) se llenan solos y los que no ({{fecha}}, {{monto}}) se
 * escriben una vez. Antes de mandar se ve a quién sí, a quién no y por qué, y cuánto cuesta. Sale de poco en poco (~40 por
 * minuto) y cada mensaje queda en la conversación de WappChat de esa persona.
 */
const EnvioMasivo: React.FC<Props> = ({ campusId, studentIds: idsIniciales, origen, referencia, fijosIniciales, onClose, onEnviado }) => {
	const http = HttpManager.getInstance();
	// Los elegidos al abrir: la pantalla de atrás limpia su selección al enviar y el título no debe quedar en "0 alumnos".
	const [studentIds] = useState<number[]>(() => [...idsIniciales]);
	const [plantillas, setPlantillas] = useState<MasivoPlantilla[] | null>(null);
	const [plId, setPlId] = useState<number>(0);
	const [fijos, setFijos] = useState<Record<string, string>>({});
	const [vista, setVista] = useState<MasivoVista | null>(null);
	const [hecho, setHecho] = useState<{ id: number; total: number; minutos: number } | null>(null);
	const [progreso, setProgreso] = useState<MasivoProgreso | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		http.prospectsService.masivoPlantillas(campusId).then((r) => setPlantillas(r.data.plantillas)).catch((e) => { setPlantillas([]); setError(errorTexto(e)); });
	}, [campusId]);
	const pl = plantillas?.find((p) => p.id === plId) || null;
	useEffect(() => {
		setVista(null); setError(null);
		const p = plantillas?.find((x) => x.id === plId);
		const pre: Record<string, string> = {};
		(p?.fijos || []).forEach((f) => { const v = fijosIniciales?.[f.clave]; if (v) pre[f.clave] = v; });
		setFijos(pre);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [plId]);
	const faltaFijo = pl?.fijos.find((f) => !(fijos[f.clave] || "").trim());
	const pedido = useMemo(() => ({ plantillaId: plId, studentIds, fijos, dept: pl?.dept || null }), [plId, studentIds, fijos, pl]);

	const revisar = async () => {
		if (!pl || faltaFijo || busy) return;
		setBusy(true); setError(null);
		try { setVista((await http.prospectsService.masivoVista(campusId, pedido)).data); }
		catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const enviar = async () => {
		if (!vista || !vista.resumen.ok || busy) return;
		setBusy(true); setError(null);
		try { setHecho((await http.prospectsService.masivo(campusId, { ...pedido, origen, referencia: referencia || null })).data); if (onEnviado) onEnviado(); }
		catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const detener = async () => {
		if (!hecho || busy) return;
		setBusy(true);
		try { setProgreso((await http.prospectsService.masivoCancelar(campusId, hecho.id)).data); } catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
		const verProgreso = async () => {
		if (!hecho) return;
		try { setProgreso((await http.prospectsService.masivoProgreso(campusId, hecho.id)).data); } catch (e) { setError(errorTexto(e)); }
	};
	// El texto de la plantilla con los datos marcados (cada quien recibe los suyos).
	const muestra = pl ? pl.cuerpo.split(/(\{\{\s*[a-z_]+\s*\}\})/g).map((t, i) => /^\{\{/.test(t)
		? <span key={i} className="wa-mas-dato">{(fijos[t.replace(/[{}\s]/g, "")] || "").trim() || t.replace(/[{}\s]/g, "")}</span> : t) : null;
	const r = vista?.resumen;
	const omitidos = vista?.personas.filter((p) => p.estado !== "ok") || [];

	return (
		<Modal show onHide={onClose} className="wa-mas-modal waModalArriba" backdropClassName="waFondoArriba" aria-labelledby="wa-mas-titulo" centered>
			<Modal.Header closeButton closeLabel="Cerrar">
				<Modal.Title id="wa-mas-titulo">Enviar WhatsApp <span className="wa-mas-n">{studentIds.length} alumno{studentIds.length === 1 ? "" : "s"}</span></Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{hecho ? (
					<div className="wa-mas-listo">
						<b>Se están enviando {hecho.total} mensaje{hecho.total === 1 ? "" : "s"}.</b>
						<span>Salen de poco en poco (unos {hecho.minutos} min). Cada uno queda en la conversación de esa persona en WappChat y las respuestas llegan a tu bandeja.</span>
						{progreso ? (
							<span className="wa-mas-prog">{progreso.envio.enviados} enviados · {progreso.envio.omitidos} sin enviar · {progreso.envio.fallidos} con error
								{progreso.envio.estado === "terminado" ? " · terminado" : progreso.envio.estado === "cancelado" ? " · detenido" : ""}</span>
						) : null}
						{progreso?.items.filter((i) => i.estado === "omitido" || i.estado === "fallido").map((i) => (
							<span key={i.student_id} className="wa-mas-omit">{i.nombre}: {i.motivo}</span>
						))}
					</div>
				) : (
					<>
						<label className="wa-mas-l" htmlFor="wa-mas-pl">Plantilla</label>
						{plantillas === null ? <div className="wa-mas-gris">Cargando…</div>
							: !plantillas.length ? <div className="wa-mas-oro">No tienes plantillas aprobadas para tu área. Créalas en Mensajería › Plantillas.</div> : (
								<select id="wa-mas-pl" value={plId} onChange={(e) => setPlId(Number(e.target.value))}>
									<option value={0}>Elige una plantilla…</option>
									{plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre + (p.categoria === "MARKETING" ? " · Promoción" : "")
										+ (!p.propia && p.territorioNombre ? " · " + p.territorioNombre : "")}</option>)}
								</select>
							)}
						{pl ? <div className="wa-mas-muestra">{muestra}</div> : null}
						{pl?.fijos.map((f) => (
							<div key={f.clave}>
								<label className="wa-mas-l" htmlFor={"wa-mas-f-" + f.clave}>{f.etiqueta} (igual para todos)</label>
								<input id={"wa-mas-f-" + f.clave} value={fijos[f.clave] || ""} placeholder={f.ejemplo} autoComplete="off"
									onChange={(e) => { setFijos((x) => ({ ...x, [f.clave]: e.target.value })); setVista(null); }} />
							</div>
						))}
						{r ? (
							<div className="wa-mas-res">
								<b>{r.ok} listo{r.ok === 1 ? "" : "s"} para enviar{r.omitidos ? " · " + r.omitidos + " no" : ""}</b>
								<span>{r.ventanaAbierta ? r.ventanaAbierta + " con la ventana abierta (sin costo) · " : ""}
									{r.ok - r.ventanaAbierta > 0 ? "≈ US$" + r.costoUsd.toFixed(2) + " en Meta · " : ""}unos {r.minutos} min en salir</span>
								{omitidos.length ? (
									<details>
										<summary>Por qué no se envía a {omitidos.length}</summary>
										{omitidos.map((p) => <span key={p.studentId} className="wa-mas-omit">{p.nombre}: {p.motivo}</span>)}
									</details>
								) : null}
							</div>
						) : null}
					</>
				)}
				{error ? <div className="wa-mas-oro" role="alert">{error}</div> : null}
			</Modal.Body>
			<Modal.Footer>
				{hecho ? (
					<>
						{progreso?.envio.estado !== "terminado" && progreso?.envio.estado !== "cancelado"
							? <button type="button" className="wa-mas-b" disabled={busy} onClick={detener}>Detener Envío</button> : null}
						<button type="button" className="wa-mas-b" onClick={verProgreso}>Ver Cómo Va</button>
						<button type="button" className="wa-mas-b naranja" onClick={onClose}>Listo</button>
					</>
				) : (
					<>
						<button type="button" className="wa-mas-b" onClick={onClose}>Cancelar</button>
						{vista && r && r.ok ? (
							<button type="button" className="wa-mas-b naranja" disabled={busy} onClick={enviar}>{busy ? "Enviando…" : "Enviar a " + r.ok}</button>
						) : (
							<button type="button" className="wa-mas-b naranja" disabled={!pl || !!faltaFijo || busy} onClick={revisar}>{busy ? "Revisando…" : "Revisar"}</button>
						)}
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default EnvioMasivo;
